import { TextInput } from 'lego-on-react';
import React, { useReducer } from 'react';

import i18n from '@yandex-int/i18n';
import * as keyset from 'i18n/Donation';
import block from 'propmods';

import { deleteDonationSettings, updateDonationSettings } from '../../api';
import { useCurrentAliceApp } from '../../hooks/useCurrentAliceApp';
import { useFetchingHandler } from '../../hooks/useFetchingHandler';
import { useFormSettings } from '../../hooks/useFormSettings';
import { useSnapshotRefresher } from '../../hooks/useSnapshotRefresher';
import { wrapInText } from '../../lib/i18n-utils';
import { DonationSettings } from '../../model/skill';
import { donationSettingsDeleteTooltipReducer, donationSettingsSaveTooltipReducer } from '../../reducers/donations';
import CopyField from '../CopyField/CopyField';
import { ErrorBoundary } from '../ErrorBoundary/ErrorBoundary';
import Description from '../Form/Description/Description';
import { Field, Header } from '../Form/Form';
import Link from '../Link/Link';
import { PageSegment } from '../PageSegment/PageSegment';
import TooltipButton from '../TooltipButton/TooltipButton';
import { PageHeadline } from '../Typo/Typo';

import WalletIcon from './assets/wallet.svg';

import './DonationPage.scss';

const b = block('DonationPage');
const t = i18n(keyset);

export const defaultDonationSettings: DonationSettings = {
    defaultSum: '100',
    walletNumber: '',
} as const;

const walletNumberDescription = (
    <Description>
        {wrapInText(
            (s) => <Link to="https://yoomoney.ru/id">{s}</Link>,
            (s) => <Link to="https://yoomoney.ru/reg">{s}</Link>,
        )(t('description-wallet'))}
    </Description>
);

const defaultSumDescription = (
    <Description>
        {wrapInText((s) => <Link to="https://yoomoney.ru/page?id=523014">{s}</Link>)(t('description-sum'))}
    </Description>
);

export const defaultSumValidator = (value: string) => {
    if (isNaN(Number(value))) {
        return t('sum-validate-is-number');
    }

    if (Number(value) > 15000) {
        return t('sum-validate-no-more-15k');
    }

    return '';
};

export const walletNumberValidator = (value: string) => {
    if (!/^41001[0-9]{6,15}$/.test(value)) {
        return t('wallet-validate-number');
    }

    return '';
};

export const DonationPage: React.FC<{ disabled: boolean }> = ({ disabled }) => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const skill = useCurrentAliceApp()!;
    const refreshSnapshot = useSnapshotRefresher();

    const { settings, onChange, errors, hasErrors, setSettings } = useFormSettings<DonationSettings>({
        defaultSettings: skill.donationSettings || defaultDonationSettings,
        validators: {
            defaultSum: defaultSumValidator,
            walletNumber: walletNumberValidator,
        },
    });

    const [saveTooltipProps, dispatchSaveTooltipAction] = useReducer(donationSettingsSaveTooltipReducer, {
        tooltipVisible: false,
    });

    const [deleteTooltipProps, dispatchDeleteTooltipAction] = useReducer(donationSettingsDeleteTooltipReducer, {
        tooltipVisible: false,
    });

    const clearTooltips = () => {
        dispatchDeleteTooltipAction('clear');
        dispatchSaveTooltipAction('clear');
    };

    const [updateSettings, savingState, initSaveHandlerState] = useFetchingHandler({
        handler: async () => {
            clearTooltips();
            await updateDonationSettings(skill.id, settings);
            await refreshSnapshot();
        },
        onError: () => dispatchSaveTooltipAction('error'),
        onLoad: () => dispatchSaveTooltipAction('ok'),
    });

    const [deleteSettings, deletingState, initDeleteHandlerState] = useFetchingHandler({
        handler: async () => {
            clearTooltips();
            await deleteDonationSettings(skill.id);
            await refreshSnapshot();
            setSettings(defaultDonationSettings);
        },
        onError: () => dispatchDeleteTooltipAction('error'),
        onLoad: () => dispatchDeleteTooltipAction('ok'),
    });

    const { defaultSum, walletNumber } = settings;

    const isSettingsValid = !hasErrors && Boolean(defaultSum) && Boolean(walletNumber);

    if (!skill.onAir) {
        return (
            <div {...b()}>
                <PageHeadline title={t('page-header-title')} />
                <div {...b('stub')}>{t('error-no-published')}</div>
            </div>
        );
    }

    const isSaving = savingState === 'loading';
    const isDeleting = deletingState === 'loading';

    return (
        <ErrorBoundary withHomepageLink>
            <div {...b()}>
                <PageHeadline title={t('page-header-title')} />
                <p>{t('page-header-description')}</p>
                <Header>{t('form-settings')}</Header>
                <Field
                    label={t('label-wallet')}
                    required
                    error={errors.walletNumber}
                    description={walletNumberDescription}
                >
                    <div {...b('wallet-number')}>
                        <TextInput
                            text={walletNumber}
                            size="s"
                            theme="normal"
                            onChange={onChange('walletNumber')}
                            disabled={disabled}
                        />
                        <div {...b('wallet-icon')}>
                            <WalletIcon />
                        </div>
                    </div>
                </Field>
                <Field
                    label={t('label-sum')}
                    error={errors.defaultSum}
                    required
                    documentationUrl="https://yandex.ru/support/money/fees-limits/transfers.html"
                    description={defaultSumDescription}
                >
                    <div {...b('default-sum')}>
                        <div {...b('default-sum-field')}>
                            <TextInput
                                size="s"
                                theme="normal"
                                text={defaultSum}
                                onChange={onChange('defaultSum')}
                                disabled={disabled}
                            />
                        </div>
                        <div {...b('currency')}>₽</div>
                    </div>
                </Field>
                <div {...b('buttons')}>
                    <span {...b('save-button')}>
                        <TooltipButton
                            size="m"
                            progress={isSaving}
                            tooltipTo="bottom"
                            onClick={updateSettings}
                            {...saveTooltipProps}
                            onOutsideClick={() => {
                                initSaveHandlerState();
                                clearTooltips();
                            }}
                            disabled={disabled || !isSettingsValid || isDeleting}
                        >
                            {t('button-save')}
                        </TooltipButton>
                    </span>
                    {Boolean(skill.donationSettings) && (
                        <span {...b('delete-button')}>
                            <TooltipButton
                                size="m"
                                theme="normal"
                                progress={isDeleting}
                                tooltipTo="bottom"
                                onClick={deleteSettings}
                                {...deleteTooltipProps}
                                onOutsideClick={() => {
                                    initDeleteHandlerState();
                                    clearTooltips();
                                }}
                                disabled={disabled || isSaving}
                            >
                                {t('button-off')}
                            </TooltipButton>
                        </span>
                    )}
                </div>
                <PageSegment
                    title={t('additional')}
                    available={Boolean(skill.donationSettings) && !skill.hideInStore}
                    stubText={getStubText(Boolean(skill.donationSettings), skill.hideInStore)}
                    offsetTop
                >
                    <CopyField
                        label={t('additional-link')}
                        text={`${__appConfig.app.storeUrl}/skills/${skill.slug}?action=donation`}
                        disabled={disabled}
                    />
                </PageSegment>
            </div>
        </ErrorBoundary>
    );
};

const getStubText = (hasDonationSettings: boolean, hideInStore: boolean) => {
    if (hideInStore) {
        return t('stub-text-hide-store');
    }

    if (!hasDonationSettings) {
        return t('stub-text-has-donat');
    }

    return t('stub-text');
};
